import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const chapters = {
  fr: [
    { n: "01", title: "Relier les territoires par l'art", body: "Une ligne artistique continue de Castets-en-Dorthe à l'Étang de Thau. Le canal devient galerie, la berge devient scène — 750 km d'expositions à ciel ouvert." },
    { n: "02", title: "30 ans UNESCO, un anniversaire manifeste", body: "1996 — 2026. Trente ans après l'inscription du chef-d'œuvre de Riquet au patrimoine mondial, l'art contemporain dialogue avec le génie civil." },
    { n: "03", title: "De la berge à la galerie", body: "De la berge à la galerie, le canal devient un fil d'Ariane. Les œuvres surgissent comme des éclats de lumière, invitant les passants à poursuivre la trace — jusqu'aux galeries, aux musées, aux ateliers où l'art respire. Nous ne brisons rien : nous prolongeons. Nous ouvrons des portes invisibles." },
    { n: "04", title: "Créer des connexions pertinentes", body: "Il ne s'agit pas d'exposer partout simplement parce qu'un espace est disponible. Nous faisons des choix exigeants, en accord avec l'esprit du Fil du Midi : des lieux qui ont du sens, des contextes qui résonnent, des implantations qui enrichissent le parcours." },
    { n: "05", title: "Fédérer les bonnes énergies", body: "Nous ne cherchons pas à rassembler indistinctement tous les acteurs. Nous voulons activer les bonnes alliances, celles qui renforcent la cohérence du projet, celles qui partagent la vision du FILM. Il s'agit de composer un réseau vivant, harmonieux." },
    { n: "06", title: "Cap 2030 · une ligne d'art continu", body: "D'ici 2030, nous visons une couverture intégrale des 750 km, assurant une présence artistique cohérente, lisible et durable — jusqu'à ce que les 750 km deviennent une ligne d'art continu. Un ruban vivant, mouvant, partagé." },
  ],
  en: [
    { n: "01", title: "Connecting territories through art", body: "A continuous artistic line from Castets-en-Dorthe to the Étang de Thau. The canal becomes a gallery, the bank becomes a stage — 750 km of open-air exhibitions." },
    { n: "02", title: "30 years of UNESCO, a manifesto anniversary", body: "1996 — 2026. Thirty years after Riquet's masterpiece was inscribed as a World Heritage Site, contemporary art enters into dialogue with civil engineering." },
    { n: "03", title: "From the bank to the gallery", body: "From the bank to the gallery, the canal becomes an Ariadne's thread. Artworks appear like flashes of light, inviting passers-by to follow the trail — to the galleries, museums and studios where art breathes. We break nothing: we extend. We open invisible doors." },
    { n: "04", title: "Creating meaningful connections", body: "It is not about exhibiting everywhere simply because a space is available. We make demanding choices, in keeping with the spirit of the Fil du Midi: places that carry meaning, contexts that resonate, locations that enrich the trail." },
    { n: "05", title: "Bringing the right energies together", body: "We do not seek to gather every player indiscriminately. We want to activate the right alliances — those that strengthen the coherence of the project, those that share the vision of FILM. It is about composing a living, harmonious network." },
    { n: "06", title: "Horizon 2030 · a continuous line of art", body: "By 2030, we aim for full coverage of the 750 km, ensuring a coherent, legible and lasting artistic presence — until the 750 km become one continuous line of art. A living, moving, shared ribbon." },
  ],
};

export default function Manifesto() {
  const { lang } = useLang();
  const en = lang === "en";
  const [openChapter, setOpenChapter] = useState(0);
  return (
    <section id="manifeste" className="relative py-32 md:py-48 px-6 md:px-12" data-testid="manifesto">
      <div className="max-w-[1400px] mx-auto">
        {/* Lead — Canal'Art 2026, dans les pas de Riquet */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.65, 0, 0.35, 1] }}
          className="max-w-6xl mb-20 md:mb-28 grid md:grid-cols-12 gap-8 md:gap-12 items-center"
          data-testid="manifesto-lead"
        >
          <div className="md:col-span-9">
            <div className="meta-label boxed text-cinabre mb-6">§ Canal'Art 2026 · {en ? "30 years UNESCO" : "30 ans UNESCO"}</div>
            <p className="font-serif italic font-light text-3xl md:text-5xl leading-[1.15] tracking-tight text-fg/90">
              {en ? (
                <>« To celebrate the <strong className="not-italic font-normal">30th anniversary</strong> of the Canal du Midi's inscription as a UNESCO World Heritage Site, <strong className="not-italic font-normal">Les Arteurs</strong> present <strong className="not-italic font-normal">Canal'Art 2026</strong> across the canal's emblematic sites — <em>in the footsteps of Riquet.</em> »</>
              ) : (
                <>« Pour célébrer le <strong className="not-italic font-normal">30ᵉ anniversaire</strong> de l'inscription du Canal du Midi au patrimoine mondial de l'UNESCO, <strong className="not-italic font-normal">Les Arteurs</strong> organisent <strong className="not-italic font-normal">Canal'Art 2026</strong> sur les sites emblématiques du canal — <em>dans les pas de Riquet.</em> »</>
              )}
            </p>
          </div>
          <a
            href="https://whc.unesco.org/fr/list/770"
            target="_blank"
            rel="noreferrer"
            className="md:col-span-3 flex md:justify-end justify-start group"
            data-testid="manifesto-unesco30-logo"
            aria-label="Canal du Midi — Patrimoine mondial UNESCO depuis 1996"
          >
            <div className="flex flex-col items-center md:items-end">
              <div className="mb-4 text-center md:text-right">
                <div className="font-serif font-light text-3xl md:text-4xl leading-none tracking-tight">UNESCO</div>
                <div className="mt-1 font-serif italic text-xl md:text-2xl text-fg/75 leading-tight">{en ? "30 years of the canal" : "30 ans du canal"}</div>
              </div>
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/y53fq32c_OIP.jpg"
                alt="30 ans UNESCO · Canal du Midi 1996-2026"
                className="w-44 md:w-56 h-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                loading="lazy"
              />
              <div className="mt-3 font-mono text-[9px] tracking-[0.24em] uppercase text-fg/55 text-center md:text-right">
                1996 → 2026 · {en ? "World Heritage" : "Patrimoine mondial"}
              </div>
            </div>
          </a>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-4 md:sticky md:top-32 self-start">
          <div className="meta-label boxed text-cinabre">§ {en ? "Manifesto" : "Manifeste"}</div>
          <h2 className="font-serif font-light text-5xl md:text-6xl mt-6 leading-[0.95] tracking-tight">
            {en ? <>From the bank<br/>to the <em className="italic">gallery</em>.</> : <>De la berge<br/>à la <em className="italic">galerie</em>.</>}
          </h2>
          <p className="mt-8 text-fg/70 leading-relaxed max-w-sm">
            {en
              ? "Art should not be a privilege. Making the bank a first step — towards the studio, the gallery, the museum. Towards the artist."
              : "L'art ne doit pas être un privilège. Faire de la berge un premier pas — vers l'atelier, la galerie, le musée. Vers l'artiste."}
          </p>

          <motion.figure
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.65, 0, 0.35, 1] }}
            className="mt-12 hidden md:block group"
            data-testid="film-signature-poster"
          >
            <div className="border border-fg/15 overflow-hidden bg-fg/[0.02]">
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/0zqwmc4d_logo%20Fil%20du%20Midi.png"
                alt="FILM · Le Fil du Midi — Le plus grand parcours artistique du monde"
                className="w-full aspect-[3/5] object-cover"
                loading="lazy"
              />
            </div>
            <figcaption className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
              FILM · Le Fil du Midi
            </figcaption>
          </motion.figure>
        </div>

        <div className="md:col-span-7 md:col-start-6 space-y-0">
          {chapters[lang].map((c, i) => (
            <motion.article
              key={c.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.04, ease: [0.65, 0, 0.35, 1] }}
              className="border-t border-fg/15"
              data-testid={`manifesto-chapter-${c.n}`}
            >
              <button
                type="button"
                onClick={() => setOpenChapter(openChapter === i ? null : i)}
                className="w-full text-left py-6 md:py-7 group flex items-start justify-between gap-6"
                data-testid={`manifesto-toggle-${c.n}`}
                aria-expanded={openChapter === i}
              >
                <div>
                  <span className="font-mono text-[11px] tracking-[0.24em] text-fg/50">§ {c.n}</span>
                  <h3 className={`font-serif text-2xl md:text-4xl leading-[1.05] tracking-tight mt-3 transition-colors ${openChapter === i ? "text-cinabre" : "group-hover:text-cinabre"}`}>
                    {c.title}
                  </h3>
                </div>
                <span className="shrink-0 mt-2 text-fg/40 group-hover:text-cinabre transition-colors">
                  {openChapter === i ? <Minus size={18} strokeWidth={1.5} /> : <Plus size={18} strokeWidth={1.5} />}
                </span>
              </button>
              <AnimatePresence initial={false}>
                {openChapter === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.65, 0, 0.35, 1] }}
                    className="overflow-hidden"
                    data-testid={`manifesto-body-${c.n}`}
                  >
                    <p className="pb-8 text-fg/80 leading-relaxed text-lg max-w-xl">{c.body}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.article>
          ))}
        </div>
        </div>
      </div>
    </section>
  );
}
